import { useState } from "react";
import { usePostHog } from "posthog-js/react";

interface ShareButtonProps {
  /** Root-relative path to the design's stitch-preview page, e.g. "/replay/{id}". */
  path: string;
  /** Which page this button was clicked from, for the PostHog event. */
  source: "create" | "convert" | "preview" | "digitize";
}

/**
 * Shares a design's stitch-preview page, not the current page and not a raw
 * file link -- the stitch preview is the one URL anyone can open to see
 * (and, once there, download) the design without whatever token or form
 * state got the sharer here. Uses the native share sheet where the browser
 * has one (mostly mobile), falling back to copying the link.
 *
 * Built from window.location.origin, not a hardcoded prod host (unlike
 * usePageMeta.ts's canonical/OG tags, which deliberately always point at
 * prod regardless of what's serving the page): dev and prod are separate
 * databases with separate storage, so a design generated on dev has no
 * equivalent on prod at all. A hardcoded prod link shared from dev would
 * 404 for whoever opened it -- the link has to point at whichever host the
 * data actually lives on.
 */
export function ShareButton({ path, source }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);
  const posthog = usePostHog();

  const handleShare = async () => {
    const url = `${window.location.origin}${path}`;
    posthog?.capture("design_share_clicked", { source, path });

    if (navigator.share) {
      try {
        await navigator.share({ url });
        // Note: the Web Share API can't distinguish "the user picked a
        // target" from "the user closed the sheet without picking one" --
        // both resolve here. method: native_share means the sheet opened
        // and didn't error, not that the link definitely went anywhere.
        posthog?.capture("design_share_completed", { source, path, method: "native_share" });
        return;
      } catch {
        // Cancelled, or the browser advertises navigator.share but refuses
        // this call -- either way, fall through to copying the link instead
        // of leaving the click looking like it did nothing.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      posthog?.capture("design_share_completed", { source, path, method: "clipboard" });
    } catch {
      // Clipboard API can be unavailable (e.g. an insecure context) -- open
      // the link itself as a last resort so the click still does something.
      window.open(url, "_blank", "noopener,noreferrer");
      posthog?.capture("design_share_completed", { source, path, method: "new_tab" });
    }
  };

  return (
    <button
      type="button"
      className="share-button"
      onClick={handleShare}
      aria-label="Share this design"
      title="Share this design"
    >
      <i className={copied ? "fa-solid fa-check" : "fa-solid fa-share-nodes"} aria-hidden="true" />{" "}
      {copied ? "Link copied" : "Share"}
    </button>
  );
}
